import type { Present } from '@yp/common/identity.ts';
import type { Scalar, Time } from '@yp/common/units.ts';
import { getContext, setContext } from 'svelte';

export const ToastVariants = ['default', 'success', 'warning', 'error'] as const;
type ToastVariant = (typeof ToastVariants)[number];

export type Toast = {
  title: string;
  text?: string;
  duration?: Scalar<Time>;
  variant?: ToastVariant;
};

export type ToastStyle = {
  icon?: string;
  fg: string;
  bg: string;
};

export function toastStyle(toast: Toast): ToastStyle {
  switch (toast.variant) {
    case 'success':
      return {
        icon: 'check_circle',
        fg: '--color-success-content',
        bg: '--color-success',
      };
    case 'warning':
      return { icon: 'warning', fg: '--color-warning-content', bg: '--color-warning' };
    case 'error':
      return { icon: 'report', fg: '--color-error-content', bg: '--color-error' };
    default:
      return { fg: '--color-info-content', bg: '--color-info' };
  }
}

export function toastColor(toast: Toast) {
  switch (toast.variant) {
    case 'success':
      return '--color-success';
    case 'warning':
      return '--';
    case 'error':
      return 'report';
    default:
      return undefined;
  }
}

const callbackContextName = 'callbacks';

export interface CallbackContext {
  signIn?: () => Promise<void>;
  toast?: (n: Toast) => void;
}
// export type CallbackContext = {
//   signIn?: () => Promise<void>;
// };

export function initCallbacks() {
  setContext<CallbackContext>(callbackContextName, {});
}

export function setCallback<K extends keyof CallbackContext>(name: K) {
  const ctx = getContext<CallbackContext>(callbackContextName);
  return (cb: CallbackContext[K]) => (ctx[name] = cb);
}

export function makeCallback<K extends keyof CallbackContext>(
  name: K,
): Present<CallbackContext[K]> {
  const ctx = getContext<CallbackContext>(callbackContextName);
  // @ts-expect-error Workaround for union type of parameters.
  return (...args: Parameters<Present<CallbackContext[K]>>) => ctx[name]?.(...args);
}
